import { ScoredAlgoliaHit } from '../../algolia/ProductClient';
import { AlgoliaProductHit } from '../../algolia/types/AlgoliaProductHit';

type GoogleRecommendedItem = {
  id: string;
  itemMetadata: {
    score: number;
  };
};

export type GoogleRecommendedItemWithVariance = GoogleRecommendedItem & {
  variance: number;
  varianceScore: number;
};

export const filterGoogleRecommendations = (
  items: Array<GoogleRecommendedItem>,
  acceptedVariancePercentage?: number
): Array<GoogleRecommendedItemWithVariance> => {
  if (!items.length) return [];
  // problems with this approach - it's currently all relative
  // if we have all bad data, it will still return the top n% of bad data in relation to the top score
  // we could instead of looking at the mean variance, look at 1 as the ideal score
  // and then look at the variance from that ideal score
  // but in this case, if we cut off results that don't fall within the % set, we may get no results
  // which has been said to not be desirable

  const sortedItems = [...items].sort((a, b) => b.itemMetadata.score - a.itemMetadata.score);

  const allScores = sortedItems.map(item => item.itemMetadata.score);
  const averageScore = allScores.reduce((sum, score) => sum + score, 0) / allScores.length;

  const bestScore = allScores[0];

  const standardDeviation =
    Math.sqrt(allScores.reduce((sum, score) => sum + Math.pow(score - bestScore, 2), 0) / allScores.length) || 1;
  const coefficientOfVariation = averageScore !== 0 ? standardDeviation / Math.abs(bestScore) : 0;

  const itemVariances = sortedItems.map(item => {
    const varianceFromBest = Math.abs(item.itemMetadata.score - bestScore);
    return {
      ...item,
      variance: varianceFromBest,
      // this multiplication by 100 is just to make the variance score a little more readable and make more sense
      varianceScore: 100 - (varianceFromBest / standardDeviation) * 100,
      googleScore: item.itemMetadata.score,
    };
  });

  if (coefficientOfVariation < 0.05) {
    return itemVariances;
  }

  itemVariances.sort((a, b) => a.variance - b.variance);

  return acceptedVariancePercentage
    ? itemVariances.filter(item => item.varianceScore >= acceptedVariancePercentage)
    : itemVariances;
};

export const getItemsByProductCategory = (
  recommendations: Array<ScoredAlgoliaHit>,
  cartItems?: Array<ScoredAlgoliaHit>
): Array<AlgoliaProductHit> => {
  const bestInCategory = new Map();
  if (!recommendations) return [];

  for (const product of recommendations) {
    const category = product.category_id || 0;

    const varianceScore = product?.varianceScore ?? -Infinity;
    if (
      !cartItems?.some(cartItem => cartItem.category_id === product.category_id) &&
      (!bestInCategory.has(category) || varianceScore > (bestInCategory.get(category)?.varianceScore ?? -Infinity))
    ) {
      bestInCategory.set(category, { varianceScore, product });
    }
  }

  return Array.from(bestInCategory.values()).map(({ product }) => product);
};
